import { CancelMinor, DuplicateMinor, ReplayMinor } from '@shopify/polaris-icons'
import React, { memo, useCallback, useContext, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { UpdateOpportunity } from 'src/constants/actionTypes'
import { OpportunityStatus } from 'src/constants/opportunity/enums'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { Opportunity } from 'src/interfaces/opportunity'
import { MenuActionItem } from 'src/interfaces/ui'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { OpportunitiesTableContext } from './OpportunitiesTable.context'

const OpportunityActionMenuComponent = () => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const {
    actionAnchor,
    opportunityToAction,
    closeOpportunityItemAction,
    clearOpportunityToAction,
    refreshOpportunities,
  } = useContext(OpportunitiesTableContext)

  const canEdit = hasPermission(UserPermission.OpportunitiesEdit)

  const handleCopyLink = useCallback(async (opportunity: Opportunity) => {
    try {
      await navigator.clipboard.writeText(opportunity.applicationUrl)
      showToast('Application link copied', { variant: 'success' })
    } catch {
      showToast('Could not copy the link. Open the opportunity and copy it manually.', {
        variant: 'error',
      })
    }
  }, [])

  const handleToggleStatus = useCallback(
    async (opportunity: Opportunity) => {
      const nextStatus =
        opportunity.status === OpportunityStatus.Open
          ? OpportunityStatus.Closed
          : OpportunityStatus.Open

      try {
        await createAsyncAction(
          dispatch,
          UpdateOpportunity.request({ opportunityId: opportunity.id, status: nextStatus }),
        )

        showToast(
          nextStatus === OpportunityStatus.Open
            ? 'Opportunity reopened. The link accepts applications again.'
            : 'Opportunity closed. Candidates already in the pool are untouched.',
          { variant: 'success' },
        )

        refreshOpportunities()
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [dispatch, refreshOpportunities],
  )

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!opportunityToAction) {
      return []
    }

    const isOpen = opportunityToAction.status === OpportunityStatus.Open

    const items: MenuActionItem[] = [
      {
        label: 'Copy application link',
        icon: <DuplicateMinor width={20} height={20} />,
        dataTestId: `opportunities-list-row-${opportunityToAction.id}-copy-link`,
        onClick: () => {
          handleCopyLink(opportunityToAction)
          closeOpportunityItemAction()
        },
      },
    ]

    if (canEdit) {
      items.push({
        label: isOpen ? 'Close opportunity' : 'Reopen opportunity',
        icon: isOpen ? (
          <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />
        ) : (
          <ReplayMinor width={20} height={20} />
        ),
        color: isOpen ? colors.COLORS_RED_500 : undefined,
        dataTestId: `opportunities-list-row-${opportunityToAction.id}-toggle-status`,
        onClick: () => {
          handleToggleStatus(opportunityToAction)
          closeOpportunityItemAction()
        },
      })
    }

    return items
  }, [opportunityToAction, canEdit, handleCopyLink, handleToggleStatus, closeOpportunityItemAction])

  return (
    <ActionMenu
      anchorEl={actionAnchor}
      itemClassName={tw`min-w-[208px]`}
      isOpen={Boolean(actionAnchor)}
      items={actionItems}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={clearOpportunityToAction}
    />
  )
}

export const OpportunityActionMenu = memo(OpportunityActionMenuComponent)
